/**
 * „Ausverkauft“ (S39): a dish marked sold out shows so on the menu on the net at once, without
 * publishing again; paper stays as it is. The mark holds for the day of the kitchen: until 4 o'clock
 * German time next morning (when the demo is reset too), then the dish is there again by itself.
 * A set menu is sold out as a whole, a dish in it alone only where the guests choose (`setSoldOut`).
 */
import type { PublishedItem, PublishedMenu, PublishedSection } from "./card-web.ts";

/** The hour (German time) at which a new day of the kitchen begins. */
export const SOLD_OUT_UNTIL_HOUR = 4;

/** The offset of German time from UTC at an instant, in ms. */
function berlinOffset(at: number): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Berlin", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }).formatToParts(new Date(at));
  const n = (type: string) => Number(parts.find((p) => p.type === type)!.value);
  return Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second")) - Math.floor(at / 1000) * 1000;
}

/** When a mark set at `now` ends: the next 4 o'clock German time. */
export function soldOutUntil(now: number): number {
  // The day of the kitchen: before 4 o'clock it is still yesterday's.
  const day = new Date(now + berlinOffset(now) - SOLD_OUT_UNTIL_HOUR * 3_600_000);
  const wall = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + 1, SOLD_OUT_UNTIL_HOUR);
  // German wall clock to UTC; twice, for the night the clocks change.
  let at = wall - berlinOffset(wall);
  at = wall - berlinOffset(at);
  return at;
}

/** A mark (S39): a dish, or a set menu as a whole (`setKey`); exactly one of them. */
export interface SoldOut {
  dish?: string;
  set?: string;
  until: number;
}

/** A set menu among the marks: the menu and its section; stored as „set:“ before it. */
export const setKey = (menu: string, section: string) => `${menu}/${section}`;
const SET = "set:";

/** The marks as stored (by dish or „set:“ and the menu's key, until when), those still in force at `now`. */
export function soldOutOf(v: unknown, now: number): SoldOut[] {
  if (!v || typeof v !== "object" || Array.isArray(v)) return [];
  return Object.entries(v as Record<string, unknown>)
    .filter((e): e is [string, number] => typeof e[1] === "number" && e[1] > now)
    .map(([key, until]) => (key.startsWith(SET) ? { set: key.slice(SET.length), until } : { dish: key, until }));
}

/** The marks as stored, by their key. */
export const soldOutStored = (marks: readonly SoldOut[]): Record<string, number> => Object.fromEntries(marks.map((m) => [m.set !== undefined ? SET + m.set : m.dish!, m.until]));

/** An item of a set menu as far as „ausverkauft“ is concerned. */
interface CourseItem {
  text?: boolean;
  or?: boolean;
  drink?: boolean;
}

/** The courses of a set menu (S32) by the items' indexes: a dish and its alternatives („oder“); texts in none. */
function coursesOf(items: readonly CourseItem[]): number[][] {
  const courses: number[][] = [];
  items.forEach((i, k) => {
    if (i.text) return;
    if (i.or && courses.length) courses[courses.length - 1]!.push(k);
    else courses.push([k]);
  });
  return courses;
}

/** The courses as „ausverkauft“ counts them, for the allergen filter too (S63). */
export const setCourses = coursesOf;

/**
 * A set menu (S32) and „ausverkauft“ (Martin, 31.10.2026): one dish of a course to choose from
 * („oder“) is sold out alone, the guests take another. Any other dish is the menu: when it is out,
 * or all of a course's alternatives are, the whole menu is. The drinks to it do not count.
 * `alone`: the items to choose from that are out; `empty`: the items that make the menu sold out.
 */
export function setSoldOut<I extends CourseItem>(items: readonly I[], out: (i: I) => boolean): { alone: number[]; empty: number[] } {
  const alone: number[] = [];
  const empty: number[] = [];
  for (const course of coursesOf(items)) {
    const gone = course.filter((k) => out(items[k]!));
    if (gone.length < course.length) alone.push(...gone);
    else if (!course.every((k) => items[k]!.drink)) empty.push(...gone);
  }
  return { alone, empty };
}

/** The items of a set menu that can be marked sold out alone: those of a course with a choice. */
export const choosable = (items: readonly CourseItem[]): number[] => coursesOf(items).filter((c) => c.length > 1).flat();

/**
 * The items to choose from that are the last of their course still there (Martin, 31.10.2026): one
 * at least stays; for none the whole menu is sold out.
 */
export const lastOfCourse = <I extends CourseItem>(items: readonly I[], out: (i: I) => boolean): number[] =>
  coursesOf(items)
    .filter((c) => c.length > 1)
    .flatMap((c) => {
      const left = c.filter((k) => !out(items[k]!));
      return left.length === 1 ? left : [];
    });

/** What is sold out now, for `markSoldOut`. */
export interface SoldOutMarks {
  dishes: ReadonlySet<string>;
  /** By the dishes' names, for menus published before S39. */
  names: ReadonlySet<string>;
  /** Set menus as a whole (`setKey`). */
  sets: ReadonlySet<string>;
  /** Their titles (`setKey` with the title), for menus published before the sections knew their keys. */
  setTitles?: ReadonlySet<string>;
}

/**
 * The published menu with what is sold out marked, for the page alone (the mark is not stored).
 * A menu published before S39 knows no dishes; there an item counts by the name of a sold-out dish.
 * A set menu (S32) is sold out as a whole, its dishes alone only where there is a choice (`setSoldOut`).
 */
export function markSoldOut(m: PublishedMenu, marks: SoldOutMarks): PublishedMenu {
  const { dishes, names, sets, setTitles } = marks;
  if (dishes.size === 0 && sets.size === 0) return m;
  const out = (i: PublishedItem) => !i.text && (i.dish ? dishes.has(i.dish) : names.has(i.name));
  const mark = (list: PublishedSection[], menu: string) =>
    list.map((s) => {
      if (s.kind !== "set") return { ...s, items: s.items.map((i) => (out(i) ? { ...i, soldOut: true as const } : i)) };
      const own = s.id ? sets.has(setKey(menu, s.id)) : Boolean(setTitles?.has(setKey(menu, s.title)));
      const { alone, empty } = setSoldOut(s.items, out);
      if (own || empty.length) return { ...s, soldOut: true as const };
      return { ...s, items: s.items.map((i, k) => (alone.includes(k) ? { ...i, soldOut: true as const } : i)) };
    });
  return { ...m, sections: mark(m.sections, m.menu), ...(m.lead ? { lead: { ...m.lead, sections: mark(m.lead.sections, m.lead.menu) } } : {}) };
}
